import { supabase } from './db';

/**
 * Detecção de edição concorrente.
 *
 * O caso: o técnico edita um serviço offline; o escritório edita o mesmo
 * serviço enquanto isso. Quando a fila sobe, o lançamento do técnico
 * sobrescreveria o do escritório em silêncio, e ninguém saberia.
 *
 * A gravação passa a ser condicionada ao carimbo de alteração que o aparelho
 * conhecia: `UPDATE ... WHERE id = ? AND updated_at = ?`. Se nenhuma linha
 * casar, alguém alterou antes — e aí o lançamento é retido em vez de aplicado.
 *
 * Condicionar no próprio UPDATE, em vez de ler e comparar, evita a corrida
 * entre a leitura e a escrita.
 */

export class ConflitoDeEdicao extends Error {
  constructor({ tabela, id, meus, servidor }) {
    super('Registro alterado por outra pessoa');
    this.name = 'ConflitoDeEdicao';
    this.conflito = true;
    this.tabela = tabela;
    this.id = id;
    this.meus = meus;         // o que este aparelho quis gravar
    this.servidor = servidor; // como o registro está agora
  }
}

/**
 * Atualiza exigindo que o registro não tenha mudado.
 *
 * Sem `carimboEsperado` a gravação é incondicional — é o caso de quem nunca
 * leu o registro antes (criação seguida de edição na mesma tela), onde não há
 * versão anterior para conflitar.
 */
export async function atualizarSeIntacto(tabela, id, campos, carimboEsperado) {
  let consulta = supabase.from(tabela).update(campos).eq('id', id);
  if (carimboEsperado) consulta = consulta.eq('updated_at', carimboEsperado);

  const { data, error } = await consulta.select('id');
  if (error) throw error;
  if (data && data.length > 0) return data;

  // Nenhuma linha casou. Pode ser conflito ou registro apagado; buscar o
  // estado atual distingue os dois e alimenta a tela de comparação.
  const { data: atual } = await supabase.from(tabela).select('*').eq('id', id).maybeSingle();

  if (!atual) {
    throw new Error('O registro foi excluído por outra pessoa.');
  }

  throw new ConflitoDeEdicao({ tabela, id, meus: campos, servidor: atual });
}

export const ehConflito = (err) => err?.conflito === true;

/** Dois valores contam como iguais para efeito de conflito. */
const mesmoValor = (a, b) => {
  // Nulo, indefinido e string vazia são a mesma ausência. O banco devolve
  // `null` onde a tela tinha `''`, e tratá-los como diferentes acusaria
  // conflito em todo campo de texto que ninguém tocou.
  const vazio = (v) => v === null || v === undefined || v === '';
  if (vazio(a) && vazio(b)) return true;
  if (typeof a === 'object' || typeof b === 'object') {
    return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  }
  // Data volta como '2026-09-16' ou '2026-09-16T00:00:00'; comparar o texto
  // cru marcaria as duas como divergentes.
  return String(a) === String(b);
};

/**
 * O conflito atrapalha, ou o registro só mudou por fora do que vamos gravar?
 *
 * `updated_at` é o carimbo da LINHA INTEIRA. Ele se move por motivos que nada
 * têm a ver com a edição em curso: um gatilho de espelho recalculando o
 * faturamento, uma migration mexendo numa coluna, o próprio app gravando
 * outra aba. Quando isso acontece com uma tela aberta há meia hora — que é o
 * normal de quem lança workflow em campo —, o salvamento seguinte falha com
 * "alterado por outra pessoa" sem que pessoa nenhuma tenha alterado.
 *
 * A pergunta certa não é "a linha mudou?", e sim "mudou ALGO QUE EU IA
 * ESCREVER?". Se o servidor continua tendo, nos campos deste envio, os mesmos
 * valores que a tela leu ao abrir, não há o que preservar: gravar por cima
 * repõe exatamente o que já estava lá, mais o que o usuário mudou.
 *
 * Se divergir em qualquer um deles, é conflito de verdade e continua retido.
 */
export const conflitoAtrapalha = (meus = {}, servidor = {}, aoAbrir = {}) =>
  Object.keys(meus || {}).some(
    (campo) => !mesmoValor(servidor?.[campo], aoAbrir?.[campo]));

// ── Resolução ──────────────────────────────────────────────────────────────

/**
 * Conflitos aguardando decisão.
 *
 * Ficam na própria fila, marcados. Não são reenviados (sobrescreveriam o outro
 * em silêncio) nem descartados (perderiam o trabalho de campo): esperam.
 */
export const listarConflitos = async (listPending, userId) => {
  const fila = await listPending(userId);
  return fila.filter((r) => r.conflito);
};

/**
 * Campos em que os dois lados discordam.
 *
 * Só entram os que o aparelho quis mudar E que estão diferentes no servidor.
 * O resto é ruído: mostrar campo idêntico só faz a pessoa procurar a diferença.
 */
export const camposEmConflito = (registro) => {
  const { meus = {}, servidor = {} } = registro?.conflito || {};
  return Object.keys(meus)
    .filter((campo) => JSON.stringify(meus[campo]) !== JSON.stringify(servidor[campo]))
    .map((campo) => ({ campo, meu: meus[campo], deles: servidor[campo] }));
};

/**
 * Aplica a decisão.
 *
 * `escolha.tipo`:
 *   'servidor' — desiste do lançamento local; nada é gravado
 *   'meu'      — grava o que o aparelho lançou, agora sem condição
 *   'campos'   — grava a mistura que a pessoa montou
 *
 * A gravação vai SEM carimbo de propósito: a pessoa já viu os dois lados e
 * decidiu. Condicionar de novo só provocaria um segundo conflito com a mesma
 * alteração que ela acabou de aprovar.
 */
export async function resolverConflito(registro, escolha) {
  const { tabela, id, meus } = registro.conflito;

  if (escolha.tipo === 'servidor') return { gravado: false };

  const campos = escolha.tipo === 'campos' ? escolha.campos : meus;
  await atualizarSeIntacto(tabela, id, campos, null);
  return { gravado: true };
}
